import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { IoClose } from "react-icons/io5";
import { FaWineBottle } from "react-icons/fa6";
import defaultImage from "../../../utils/DefaultImage";

const ImagePreviewModal = ({
    imageUrl,
    onClose,
    alt = "Wine Preview",
    fallbackImage = defaultImage,
    usePortal = true,
}) => {
    const [isImageLoading, setIsImageLoading] = useState(true);

    // Reset loading state and handle Escape key + body scroll lock
    useEffect(() => {
        if (!imageUrl) return;

        setIsImageLoading(true);

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose?.();
            }
        };

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [imageUrl, onClose]);

    if (!imageUrl) return null;

    const modalContent = (
        <div
            className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 transition-all duration-300"
            onClick={onClose}
        >
            {/* Modal Card */}
            <div
                className="relative bg-gradient-to-b from-white via-[#FCFAFA] to-[#F9F5F6] rounded-3xl shadow-2xl border border-[#8B2433]/25 p-5 sm:p-6 flex flex-col items-center justify-center max-w-[92vw] max-h-[92vh] animate-in fade-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute -top-3.5 -right-3.5 w-9 h-9 sm:w-10 sm:h-10 bg-white border border-[#8B2433]/25 text-[#8B2433] hover:bg-[#8B2433] hover:text-white rounded-full flex items-center justify-center font-bold shadow-lg hover:shadow-xl transition-all duration-200 z-30 cursor-pointer group"
                    title="Chiudi"
                    aria-label="Chiudi anteprima immagine"
                >
                    <IoClose className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90" />
                </button>

                {/* Top Header Badge */}
                <div className="flex items-center justify-between w-full mb-3 px-1">
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#8B2433] animate-pulse"></span>
                        <span className="text-[11px] uppercase tracking-widest font-semibold poppins-medium text-[#8B2433]">
                            Anteprima Bottiglia
                        </span>
                    </div>
                </div>

                {/* Showcase Stage: Pure white background so the wine image blends seamlessly with zero square borders */}
                <div className="relative w-[280px] h-[400px] xs:w-[320px] xs:h-[460px] sm:w-[350px] sm:h-[510px] md:w-[380px] md:h-[550px] max-w-[85vw] max-h-[72vh] flex items-center justify-center overflow-hidden rounded-2xl bg-white border border-[#8B2433]/15 shadow-sm">
                    {/* Skeleton Loader: Matches the exact same stage size as shown in the preview image */}
                    {isImageLoading && (
                        <div className="absolute inset-0 w-full h-full p-3 sm:p-4 bg-white z-10 flex flex-col items-center justify-center">
                            <div className="relative w-full h-full overflow-hidden rounded-xl">
                                <Skeleton
                                    containerClassName="block w-full h-full"
                                    className="block w-full h-full"
                                    height="100%"
                                    width="100%"
                                    borderRadius="0.75rem"
                                    baseColor="#F3ECEE"
                                    highlightColor="#FFFFFF"
                                />
                                {/* Ambient Wine Bottle Watermark Placeholder in Premium Burgundy */}
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                    <FaWineBottle className="w-16 h-16 sm:w-20 sm:h-20 text-[#8B2433]/30 animate-pulse" />
                                    <span className="text-[11px] font-semibold poppins-medium text-[#8B2433]/60 mt-3 tracking-[0.2em] uppercase">
                                        Caricamento...
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Wine Image: Fully fills the stage seamlessly with zero square background clipping */}
                    <img
                        src={imageUrl}
                        onLoad={() => setIsImageLoading(false)}
                        onError={(e) => {
                            setIsImageLoading(false);
                            e.target.onerror = null;
                            e.target.src = fallbackImage;
                        }}
                        alt={alt}
                        className={`w-full h-full object-contain p-2 sm:p-3 mix-blend-multiply select-none transition-all duration-300 ease-out ${
                            isImageLoading ? "opacity-0 scale-95 pointer-events-none" : "opacity-100 scale-100"
                        }`}
                    />
                </div>
            </div>
        </div>
    );

    if (usePortal && typeof document !== "undefined") {
        return createPortal(modalContent, document.body);
    }

    return modalContent;
};

ImagePreviewModal.propTypes = {
    imageUrl: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    alt: PropTypes.string,
    fallbackImage: PropTypes.string,
    usePortal: PropTypes.bool,
};

export default ImagePreviewModal;
